// src/components/TokenCard.jsx

import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FaEthereum, FaExternalLinkAlt, FaInfoCircle, FaShoppingCart } from 'react-icons/fa';
import GlassButton, { GlassIconButton } from './ui/GlassButton';

const TokenCard = React.forwardRef(({ token, onMoreDetails, onPurchase, onViewOnChain }, ref) => {
    const [isHovered, setIsHovered] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        setMousePosition({ x, y });
    };

    const rotateX = isHovered ? (mousePosition.y - 0.5) * -10 : 0;
    const rotateY = isHovered ? (mousePosition.x - 0.5) * 10 : 0;

    return (
        <motion.div
            ref={ref}
            className="relative group h-full"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onMouseMove={handleMouseMove}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            style={{ perspective: '1000px' }}
        >
            {/* Outer glow effect on hover */}
            <div className={`absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 rounded-2xl blur-md opacity-0 group-hover:opacity-40 transition-all duration-500`} />
            
            <motion.div
                className="relative h-full bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-xl flex flex-col"
                style={{
                    transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.15s ease-out',
                }}
            >
                {/* Subtle noise texture on card */}
                <div 
                    className="absolute inset-0 opacity-[0.02] pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                    }}
                />
                
                {/* Animated border gradient */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                
                {/* Spotlight effect on hover */}
                <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"
                    style={{
                        background: `radial-gradient(circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, rgba(34,211,238,0.1) 0%, transparent 50%)`,
                    }}
                />
                
                {/* Image Container */}
                <div className="relative overflow-hidden">
                    <img
                        src={`/images/${token.image}`}
                        alt={token.name}
                        className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                    />
                    {/* Image overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/20 to-transparent" />
                    
                    {/* Price badge */}
                    <div className="absolute top-3 right-3 px-3 py-1.5 bg-black/60 backdrop-blur-md rounded-full border border-amber-500/30">
                        <span className="text-lg font-bold bg-gradient-to-r from-amber-300 to-yellow-500 bg-clip-text text-transparent">
                            {token.price}
                        </span>
                    </div>
                    
                    {/* Network badge */}
                    <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-full border border-cyan-500/30 flex items-center gap-1.5">
                        <FaEthereum className="text-cyan-400 text-sm" />
                        <span className="text-xs text-cyan-300 font-medium">ETH</span>
                    </div>
                </div>
                
                {/* Content Container */}
                <div className="p-5 flex flex-col flex-1 relative z-10">
                    {/* Token Name */}
                    <h3 className="text-xl font-bold bg-gradient-to-r from-white via-cyan-100 to-white bg-clip-text text-transparent mb-2">
                        {token.name}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-gray-300 text-sm line-clamp-2 mb-4 flex-grow">
                        {token.description}
                    </p>
                    
                    {/* Token Info Tags */}
                    <div className="flex flex-wrap gap-2 mb-5">
                        <span className="px-2.5 py-1 text-xs bg-cyan-500/10 text-cyan-400 rounded-full border border-cyan-500/20 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                            ERC-20
                        </span>
                        <span className="px-2.5 py-1 text-xs bg-purple-500/10 text-purple-400 rounded-full border border-purple-500/20 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
                            Mainnet
                        </span>
                    </div>
                    
                    {/* Action Buttons - Premium Glassmorphic Style */}
                    <div className="flex gap-3 mt-auto">
                        <GlassButton
                            variant="blue"
                            onClick={() => onMoreDetails(token.id)}
                            className="flex-1"
                        >
                            <FaInfoCircle className="text-xs" />
                            Details
                        </GlassButton>
                        
                        <GlassButton
                            variant="green"
                            onClick={() => onPurchase(token.name)}
                            className="flex-1"
                        >
                            <FaShoppingCart className="text-xs" />
                            Buy
                        </GlassButton>
                        
                        <GlassIconButton
                            onClick={() => onViewOnChain(token.contractAddress)}
                        >
                            <FaExternalLinkAlt className="text-sm" />
                        </GlassIconButton>
                    </div>
                </div>
                
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </motion.div>
        </motion.div>
    );
});

TokenCard.propTypes = {
    token: PropTypes.object.isRequired,
    onMoreDetails: PropTypes.func.isRequired,
    onPurchase: PropTypes.func.isRequired,
    onViewOnChain: PropTypes.func.isRequired,
};

export default TokenCard;
